import { useProducts, useStoreProductPrices } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Paper, Stack, TablePagination, TextField, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'
import Page from '../../components/Page'
import { StorePricingGrid } from '../../components/routes/store-pricing/StorePricingGrid'
import { InternalCategoryPicker } from '../../utils/dashboard/internalCategoryPicker'
import { StoresPicker } from '../../utils/dashboard/storesPicker'
import { fonts } from '../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/store-pricing')({
  component: StorePricingPage,
})

function StorePricingPage() {
  const [currentStore, setCurrentStore] = useState<number>(3)
  const [currentInternalCategory, setCurrentInternalCategory] = useState<number>(0)
  const [search, setSearch] = useState('')

  const { data: products } = useProducts()
  const { data: storeProductPrices, refetch: refetchPrices } = useStoreProductPrices(currentStore)

  const allProducts = useMemo(
    () => (products ? products.filter((p) => p.isActive) : []),
    [products],
  )

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setRowsPerPage(Number.parseInt(event.target.value, 10))
      setPage(0)
    },
    [setRowsPerPage, setPage],
  )

  const storeProductPricesMap = useMemo(() => {
    const map: Record<
      string,
      {
        sellPrice?: number
        franco?: number
        coldStartStockMin?: number
        agPromo?: number
      }
    > = {}

    if (storeProductPrices) {
      storeProductPrices.forEach((pricing) => {
        map[pricing.productReference] = {
          agPromo: pricing.agPromo ?? undefined,
          coldStartStockMin: pricing.coldStartStockMin ?? undefined,
          franco: pricing.franco ?? undefined,
          sellPrice: pricing.sellPrice ?? undefined,
        }
      })
    }

    return map
  }, [storeProductPrices])

  const currentCategoryProducts = useMemo(() => {
    if (!allProducts) {
      return []
    }
    if (currentInternalCategory === 0) {
      return allProducts
    }
    return allProducts.filter((product) => product.internalCategory.id === currentInternalCategory)
  }, [currentInternalCategory, allProducts])

  const currentSearchProducts = useMemo(() => {
    if (!currentCategoryProducts) {
      return []
    }
    return currentCategoryProducts.filter((product) => includesLowerCase(product.label, search))
  }, [currentCategoryProducts, search])

  const productsPaginated = useMemo(
    () => currentSearchProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [currentSearchProducts, page, rowsPerPage],
  )

  const handleChangeCategory = useCallback(
    (value: number) => {
      setPage(0)
      setCurrentInternalCategory(value)
    },
    [setPage, setCurrentInternalCategory],
  )

  const handleStoreChange = useCallback((storeId: number) => {
    setCurrentStore(storeId)
    setPage(0)
  }, [])

  return (
    <Page sx={fonts.roboto}>
      <div style={{ display: 'flex', paddingBottom: 20, paddingLeft: 20 }}>
        <Typography style={fonts.pageTitle} flexGrow={1}>
          Gestion des prix
        </Typography>
      </div>
      <Stack gap={1} p={1} flexDirection="row">
        <StoresPicker
          setUniqueStore={handleStoreChange}
          allowAllStores={false}
          defaultStore={currentStore}
        />
        <InternalCategoryPicker setCurrentCategory={handleChangeCategory} />
      </Stack>
      <Paper
        sx={{
          marginTop: '16px',
        }}
      >
        <div style={{ alignItems: 'center', display: 'flex', padding: '16px' }}>
          <TextField
            variant="outlined"
            label="Recherche"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
            }}
          />
          <div style={{ flexGrow: 1 }} />
        </div>
        <StorePricingGrid
          storeId={currentStore}
          products={productsPaginated}
          storeProductPricesMap={storeProductPricesMap}
          refetch={refetchPrices}
        />
        <TablePagination
          component="div"
          count={currentSearchProducts.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Page>
  )
}
